/**
 * Which nav entries this shell, for this user, should show.
 *
 * TWO FILTERS, IN ORDER, and they answer different questions:
 *
 *   SHELL first  — "is this part of the product I am currently in?" A Beats user
 *                  looking at beats.lumnian.com has no business seeing Deliver
 *                  even if they also own Publish; that lives at its own host.
 *                  In the house, the answer is "none of it" — see `allowed`.
 *   ENTITLEMENT second — "have I bought this at all?" Which is why a product
 *                  shell you do not hold shows an empty nav rather than a
 *                  working one; `shellIsUnavailable` is how the shell notices.
 *
 * A PRODUCT YOU DO NOT OWN IS HIDDEN, NOT LOCKED. This is the opposite of the
 * rule for cross-product features inside a product you DO own, which are shown
 * locked because that is a teaching moment arriving when it is relevant. A whole
 * product advertised in the sidebar of an app you did not buy is just clutter,
 * and clutter is the thing this split exists to remove. The launcher is where
 * the catalog lives.
 *
 * NOT ACCESS CONTROL — hiding an entry stops someone stumbling in, not someone
 * typing the URL. `require_product` on the backend is what enforces.
 *
 * Pure and dependency-free so CRA's jest can test it; the sections are passed in
 * rather than imported so this needs nothing from the routed component that
 * owns them.
 */
import type { Product } from '../types';
import type { Shell } from '../shell/shell';

export interface ProductScoped {
  /** Absent means "always visible" — the shared spine. */
  product?: Product;
  /** For the shared spine: product shells it stays out of. Recon makes nothing
   *  for the artifacts shelf but share links, and each room lists its own. */
  notIn?: Product[];
}

export interface FilterableSection<I extends ProductScoped> extends ProductScoped {
  key: string;
  items: I[];
}

/**
 * Does this product-scoped thing belong in this shell, for this user?
 *
 * EXPORTED because the nav is not the only surface that has to answer it.
 * Settings asked the same question with a different rule — entitlement only —
 * so a Brainstorms customer who also owned Publish opened Settings inside
 * Brainstorms and found GitHub deploy keys and page-spec editors. Two rules for
 * one question is how those drift; there is one now.
 */
export function visibleForShell(
  scoped: ProductScoped,
  shellProduct: Product | null,
  held: Product[],
): boolean {
  // Shared spine — Home, the shelf — except where it says it does not belong.
  if (!scoped.product) return !(shellProduct !== null && scoped.notIn?.includes(shellProduct));

  // A product shell shows exactly its own product.
  if (shellProduct !== null) return scoped.product === shellProduct;

  // THE HOUSE SHOWS NO PRODUCT WORK AT ALL, and this is the line that decides
  // whether any of this was worth doing.
  //
  // It first showed everything the user held, which sounds accommodating and
  // quietly undid the whole exercise: someone who owns all three saw the same
  // undivided app they had before, so the separation existed only for people
  // who had bought less. `app.lumnian.com` is the launcher, the shelf, settings
  // and your account — the products are somewhere else, by name, at their own
  // hostnames, and the launcher is how you get there.
  //
  // `held` still matters, just not here: the launcher uses it to decide which
  // cards link somewhere and which only describe.
  return false;
}

/**
 * Sections to render, with their items filtered too.
 *
 * A section that ends up with no items is dropped entirely — a heading over
 * nothing reads as a broken screen rather than an empty one.
 */
export function visibleSections<I extends ProductScoped, S extends FilterableSection<I>>(
  sections: S[],
  shellProduct: Product | null,
  held: Product[],
): S[] {
  return sections
    .filter((section) => visibleForShell(section, shellProduct, held))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => visibleForShell(item, shellProduct, held)),
    }))
    .filter((section) => section.items.length > 0);
}

/** Products the launcher should offer, in catalog order, minus what is held. */
export function unheldProducts(available: Product[], held: Product[]): Product[] {
  return available.filter((p) => !held.includes(p));
}

/** True when this shell is one a user without the product should not be in —
 *  used to point them at the launcher instead of an empty app. */
export function shellIsUnavailable(
  shell: Shell,
  shellProduct: Product | null,
  held: Product[],
  loading: boolean,
): boolean {
  if (loading || shell === 'app' || shellProduct === null) return false;
  return !held.includes(shellProduct);
}
